
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { normalizePlan } from '@/components/admin/subscription/PlanOptions';

interface UseSubscriptionStatusChangeProps {
  currentPlan: string;
  customerId: string;
  subscriptionId: string | null;
  onUpdatePlan: (subscriptionId: string, newPlan: string) => Promise<boolean>;
  onCreateSubscription: (userId: string, plan: string) => Promise<boolean>;
  onShowCancelDialog: () => void;
}

export const useSubscriptionStatusChange = ({
  currentPlan,
  customerId,
  subscriptionId,
  onUpdatePlan,
  onCreateSubscription,
  onShowCancelDialog,
}: UseSubscriptionStatusChangeProps) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const { toast } = useToast();
  
  // Normalize the current plan to lowercase for comparison
  const normalizedCurrentPlan = normalizePlan(currentPlan);
  
  console.log(`useSubscriptionStatusChange - currentPlan: ${currentPlan}, normalized: ${normalizedCurrentPlan}`);
  
  const handleChange = async (newValue: string) => {
    // Log the requested change
    console.log(`Plan change requested: ${normalizedCurrentPlan} -> ${newValue}`);
    
    if (newValue.toLowerCase() === normalizedCurrentPlan) {
      console.log(`No change needed: current plan ${normalizedCurrentPlan} matches requested plan ${newValue}`);
      return;
    }
    
    // If user selects cancel, show confirmation dialog
    if (newValue === "canceled") {
      console.log("Cancel option selected, showing confirmation dialog");
      onShowCancelDialog();
      return;
    }
    
    setIsUpdating(true);
    
    try {
      let success = false;
      
      if (subscriptionId) {
        // Update existing subscription
        console.log(`Updating existing subscription ${subscriptionId} to ${newValue}`);
        success = await onUpdatePlan(subscriptionId, newValue);
      } else {
        // Create new subscription for user
        console.log(`Creating new subscription for user ${customerId} with plan ${newValue}`);
        success = await onCreateSubscription(customerId, newValue);
      }
      
      if (!success) {
        console.error("Subscription update failed");
        toast({
          title: "Error",
          description: 'Failed to update subscription status',
          variant: "destructive"
        });
      } else {
        console.log("Subscription updated successfully");
        toast({
          title: "Success",
          description: 'Subscription updated successfully',
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Error updating subscription status:', error);
      toast({
        title: "Error",
        description: 'An unexpected error occurred while updating the subscription',
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return { handleChange, isUpdating, setIsUpdating, normalizedCurrentPlan };
};
